import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';

// ─────────────────────────────────────────────────────────────────────────────
// Store partagé Cockpit + CRM. Le Cockpit et le CRM manipulent les MÊMES
// comptes (grands comptes > entités > équipes) : la saisie n'est jamais à
// refaire deux fois. Persistance locale par organisation, comme la bêta
// Cockpit ; le branchement Supabase suivra (docs/ROADMAP_COPILOTE_BM.md).
// ─────────────────────────────────────────────────────────────────────────────

const CockpitDataContext = createContext(null);

// Base vide : l'utilisateur saisit ses vraies données via les formulaires.
export const emptyDb = () => ({
  clients: [], pos: [], cons: [], needs: [], shortlists: [],
  fiches: [], actions: [],
  nextId: 1, nextCid: 1, nextNid: 1, nextSid: 1, nextFid: 1, nextAid: 1,
});

const clone = (o) => JSON.parse(JSON.stringify(o));

// Complète une base chargée avec les clés absentes (montées de version sans
// perte : une base écrite par une version antérieure reste lisible).
const hydrate = (d) => {
  const base = emptyDb();
  if (!d || typeof d !== 'object') return base;
  return {
    ...base,
    ...d,
    clients: d.clients || base.clients,
    pos: d.pos || base.pos,
    cons: d.cons || base.cons,
    needs: d.needs || base.needs,
    shortlists: d.shortlists || base.shortlists,
    fiches: d.fiches || base.fiches,
    actions: d.actions || base.actions,
    nextId: d.nextId || base.nextId,
    nextCid: d.nextCid || base.nextCid,
    nextNid: d.nextNid || base.nextNid,
    nextSid: d.nextSid || base.nextSid,
    nextFid: d.nextFid || base.nextFid,
    nextAid: d.nextAid || base.nextAid,
  };
};

export function CockpitDataProvider({ children }) {
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id || 'default';
  const LS = `skilldeck_cockpit_v4:${orgId}`;

  const [db, setDb] = useState(null);
  const loadedKey = useRef(null);

  // Chargement (par organisation) — démarre vide.
  useEffect(() => {
    let d = null;
    try { d = JSON.parse(localStorage.getItem(LS)); } catch (e) { /* vide ou illisible */ }
    setDb(hydrate(d));
    loadedKey.current = LS;
  }, [LS]);

  // Persistance — jamais avant que la clé courante ait été chargée, sinon on
  // écraserait les données d'une organisation avec celles d'une autre.
  useEffect(() => {
    if (db && loadedKey.current === LS) {
      try { localStorage.setItem(LS, JSON.stringify(db)); } catch (e) { /* quota plein */ }
    }
  }, [db, LS]);

  const update = useCallback((fn) => {
    setDb((prev) => { if (!prev) return prev; const n = clone(prev); fn(n); return n; });
  }, []);

  return (
    <CockpitDataContext.Provider value={{ db, setDb, update, orgId }}>
      {children}
    </CockpitDataContext.Provider>
  );
}

export const useCockpitDb = () => {
  const ctx = useContext(CockpitDataContext);
  if (!ctx) throw new Error('useCockpitDb must be used within a CockpitDataProvider');
  return ctx;
};
